import { useState } from "react";
import { Link } from "react-router-dom";
import SavingsCalculator from "@/components/SavingsCalculator";

function HowItWorksPage() {
  const [showCalculator, setShowCalculator] = useState(false);
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Link to="/" className="flex items-center mr-4">
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold">SG</span>
                </div>
              </Link>
              <h1 className="text-xl font-bold">How It Works</h1>
            </div>
            <div className="flex items-center space-x-4">
              <Link
                to="/"
                className="text-blue-600 hover:text-blue-700 font-medium">
                Home
              </Link>
              <button
                onClick={() => setShowCalculator(true)}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700">
                Calculate Savings
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl lg:text-4xl font-bold text-center mb-6">
            How ShopGlobal Pro Works - Simple as Shopping Locally
          </h2>
          <p className="text-center text-gray-600 mb-12 text-lg">
            Receive packages from US stores as if they were shipped in Canada.
            Save 40-80% on every shipment.
          </p>

          <div className="space-y-8">
            {/* Step 1 */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex items-start space-x-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center text-3xl">
                    1️⃣
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold mb-4">
                    Sign Up (2 Minutes)
                  </h3>
                  <p className="text-gray-700 text-lg mb-4">
                    Create your free account and get your unique Delaware
                    warehouse address. This is your American shipping address
                    that saves you money on every purchase.
                  </p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="font-mono text-sm text-blue-600">
                      JOHNDOE-123@delawarewarehouse.shopglobalpro.com
                    </p>
                    <p className="text-sm text-gray-600 mt-2">
                      Use this address when ordering from US retailers like
                      Amazon, Best Buy, Nike, and more.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex items-start space-x-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center text-3xl">
                    🛒
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold mb-4">
                    Shop & Ship (Business as Usual)
                  </h3>
                  <p className="text-gray-700 text-lg mb-4">
                    Shop any US store and use your Delaware address as the
                    shipping destination. Pay their shipping costs like normal -
                    we'll handle the rest efficiently.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-blue-800 mb-2">
                        Supported Retailers
                      </h4>
                      <ul className="text-blue-700 text-sm space-y-1">
                        <li>✅ Amazon</li>
                        <li>✅ Best Buy</li>
                        <li>✅ Walmart</li>
                        <li>✅ Target</li>
                        <li>✅ Nike</li>
                        <li>✅ iHerb</li>
                      </ul>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-green-800 mb-2">
                        We Work Automatically
                      </h4>
                      <ul className="text-green-700 text-sm space-y-1">
                        <li>📧 Email detection</li>
                        <li>🚛 Package reception</li>
                        <li>📱 Status updates</li>
                        <li>🚀 Consolidation</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex items-start space-x-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center text-3xl">
                    🚚
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold mb-4">
                    We Consolidate Your Packages
                  </h3>
                  <p className="text-gray-700 text-lg mb-4">
                    Multiple packages become one shipment. Pay shipping once
                    instead of 3-5 times. Our AI intelligently groups packages
                    for maximum savings.
                  </p>
                  <div className="bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-lg">
                    <div className="flex items-center justify-center mb-4">
                      <div className="text-center">
                        <div className="text-4xl font-bold text-green-600 mb-2">
                          67%
                        </div>
                        <div className="text-green-800">
                          Average Shipping Savings
                        </div>
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-gray-900">
                          3
                        </div>
                        <div className="text-gray-600">Separate packages</div>
                        <div className="text-red-600 font-semibold">
                          $75 total
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">
                          1
                        </div>
                        <div className="text-gray-600">
                          Consolidated shipment
                        </div>
                        <div className="text-green-600 font-semibold">
                          $25 total
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 4 */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex items-start space-x-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center text-3xl">
                    📦
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold mb-4">
                    Receive in Canada (7-12 Days Total)
                  </h3>
                  <p className="text-gray-700 text-lg mb-4">
                    Packages arrive at your Canadian address. Track everything
                    in real-time through our mobile app or web dashboard. Fast,
                    reliable, professional service.
                  </p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold mb-2">Timeline Breakdown:</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>🛒 US Store → Delaware Warehouse:</span>
                        <span className="font-semibold">2-4 days</span>
                      </div>
                      <div className="flex justify-between">
                        <span>🚛 Processing & Consolidation:</span>
                        <span className="font-semibold">1-2 days</span>
                      </div>
                      <div className="flex justify-between">
                        <span>🚀 Delaware → Your Canadian Address:</span>
                        <span className="font-semibold">4-6 days</span>
                      </div>
                      <hr className="border-gray-300" />
                      <div className="flex justify-between font-bold text-blue-600">
                        <span>Total: Same as "Ship to Canada" option</span>
                        <span>But 60% Cheaper! 🎉</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl shadow-xl p-8 text-center mt-12 text-white">
            <h3 className="text-3xl font-bold mb-4">Ready to Save Big?</h3>
            <p className="text-xl mb-8 opacity-90">
              Join 7,842 Canadians already saving thousands annually on
              international shipping.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/signup"
                className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 shadow-lg">
                Get Started Free - Save Money Now
              </Link>
              <button
                onClick={() => setShowCalculator(true)}
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-blue-600">
                Calculate Your Exact Savings
              </button>
            </div>
            <p className="text-sm opacity-75 mt-4">
              ✦ Free 45-day storage ✦ No monthly fees ✦ Cancel anytime ✦ 24/7
              support
            </p>
          </div>
        </div>
      </div>

      {/* Calculator Modal */}
      {showCalculator && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="relative">
            <button
              onClick={() => setShowCalculator(false)}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 text-xl">
              ✕
            </button>
            <SavingsCalculator />
          </div>
        </div>
      )}
    </div>
  );
}

export default HowItWorksPage;
